import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import './Certificates.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Certificates = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/certificates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.success && data.certificates) {
          setCerts(data.certificates);
        } else {
          setCerts([
            { _id: '1', vesselName: 'MV Ocean Star', type: 'GMDSS', issueDate: '2023-05-15', expiryDate: '2024-05-14', status: 'valid', classSociety: 'DNV' },
            { _id: '2', vesselName: 'MT Gulf Spirit', type: 'Safety Equipment', issueDate: '2023-06-20', expiryDate: '2024-06-19', status: 'valid', classSociety: 'LR' },
            { _id: '3', vesselName: 'LNG Navigator', type: 'Radio License', issueDate: '2023-01-10', expiryDate: '2024-01-09', status: 'expiring', classSociety: 'ABS' },
            { _id: '4', vesselName: 'CS Atlantic', type: 'Navigation', issueDate: '2022-11-05', expiryDate: '2023-11-04', status: 'expired', classSociety: 'DNV' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching certificates:', error);
        setCerts([
          { _id: '1', vesselName: 'MV Ocean Star', type: 'GMDSS', issueDate: '2023-05-15', expiryDate: '2024-05-14', status: 'valid', classSociety: 'DNV' },
          { _id: '2', vesselName: 'MT Gulf Spirit', type: 'Safety Equipment', issueDate: '2023-06-20', expiryDate: '2024-06-19', status: 'valid', classSociety: 'LR' },
          { _id: '3', vesselName: 'LNG Navigator', type: 'Radio License', issueDate: '2023-01-10', expiryDate: '2024-01-09', status: 'expiring', classSociety: 'ABS' },
          { _id: '4', vesselName: 'CS Atlantic', type: 'Navigation', issueDate: '2022-11-05', expiryDate: '2023-11-04', status: 'expired', classSociety: 'DNV' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);

  const filteredCerts = certs.filter(cert => {
    const matchesSearch = cert.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          cert.type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || cert.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: certs.length,
    valid: certs.filter(c => c.status === 'valid').length,
    expiring: certs.filter(c => c.status === 'expiring').length,
    expired: certs.filter(c => c.status === 'expired').length
  };

  const getDaysLeft = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days === 0) return 'Today';
    return `${days} days`;
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'valid': return 'valid';
      case 'expiring': return 'expiring';
      case 'expired': return 'expired';
      default: return 'valid';
    }
  };

  if (loading) return <div className="loading-spinner">Loading certificates...</div>;

  return (
    <div className="certificates-page">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-certificate"></i> Documents & Certificates</h1>
          <p>Manage and monitor your vessel certificates</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-certificate"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Certificates</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.valid}</div>
            <div className="stat-label">Valid</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-clock"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.expiring}</div>
            <div className="stat-label">Expiring Soon</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-exclamation-triangle"></i></div>
          <div className="stat-info">
            <div className="stat-value">{stats.expired}</div>
            <div className="stat-label">Expired</div>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
          <button className={`filter-tab ${filterStatus === 'valid' ? 'active' : ''}`} onClick={() => setFilterStatus('valid')}>Valid</button>
          <button className={`filter-tab ${filterStatus === 'expiring' ? 'active' : ''}`} onClick={() => setFilterStatus('expiring')}>Expiring Soon</button>
          <button className={`filter-tab ${filterStatus === 'expired' ? 'active' : ''}`} onClick={() => setFilterStatus('expired')}>Expired</button>
        </div>
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search certificates..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="table-container">
        <table className="certificates-table">
          <thead>
            <tr>
              <th>Certificate ID</th>
              <th>Vessel</th>
              <th>Type</th>
              <th>Class Society</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredCerts.map(cert => (
              <tr key={cert._id}>
                <td>{cert._id?.slice(-8) || 'N/A'}</td>
                <td>{cert.vesselName}</td>
                <td>{cert.type}</td>
                <td>{cert.classSociety}</td>
                <td>{new Date(cert.issueDate).toLocaleDateString()}</td>
                <td>
                  {new Date(cert.expiryDate).toLocaleDateString()}
                  <span style={{ fontSize: '11px', marginLeft: '8px', color: 'var(--text-tertiary)' }}>
                    ({getDaysLeft(cert.expiryDate)})
                  </span>
                </td>
                <td>
                  <span className={`cert-status ${getStatusClass(cert.status)}`}>
                    {cert.status.toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCerts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
            <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></i>
            <p>No certificates found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificates;